import Box from "@mui/material/Box";
import WestIcon from "@mui/icons-material/West";
import EastIcon from "@mui/icons-material/East";
import Drawer from "@mui/material/Drawer";
import InsertPhotoOutlinedIcon from "@mui/icons-material/InsertPhotoOutlined";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LessonsTags } from "models/LessonsTags";
import { LessonType } from "models/LessonType";
import { useAuthContext } from "hooks/useAuthContext";
import { UserRoles } from "constants/userRoles";
import { useTheme, styled } from "@mui/material/styles";
import { LessonFormValidationErrors } from "models/LessonFormValidationErrors";
import { LessonForm } from "components/views/editor/models/LessonForm";
import { convertImageFileToHtmlImage } from "helpers/fileHelper";
import {
    TitleTypography,
    SubtitleTypography,
    SpacedTypography,
    ImageUploadBox,
    ImagePreview,
    ImagePlaceholderBox,
    ErrorTypography,
    FieldTypography,
    StyledTextField,
    StyledAutocomplete,
    DescriptionTextField,
    FormContainerBox,
    ToggleDrawerButton,
    StyledDivider
} from "./LessonFormDrawer.styles";
import { RewardType } from "@models/RewardType";
import { Feedback } from "@api/models/INode";
import { FEEDBACK_MAX_LENGTH } from "constants/feedback";

const StyledHiddenInput = styled('input')({
    display: "none"
});

interface LessonFormDrawerProps {
    tags: LessonsTags;
    lessonFormData: LessonForm;
    formValidationErrors: LessonFormValidationErrors;
    onLessonDataChange: (lessonFormField: keyof LessonForm, newLessonFormData: LessonForm) => void;
    title: string;
    showVersionChip?: boolean;
    isDefaultOpen?: boolean;
    disableLessonTypeChange?: boolean;
}

export const LessonFormDrawer = (props: LessonFormDrawerProps) => {
    const {
        tags,
        lessonFormData,
        formValidationErrors,
        onLessonDataChange,
        title,
        showVersionChip = true,
        isDefaultOpen,
        disableLessonTypeChange = false
    } = props;

    const [drawerOpen, setDrawerOpen] = useState<boolean>(!!props.isDefaultOpen);
    const imageFileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const { userState } = useAuthContext();
    const isAdmin = userState.user.selectedWorkspace.userRole === UserRoles.ADMIN;
    const theme = useTheme();
    const acceptedImageMimeTypes = "image/png, image/jpeg, image/jpg";

    const toggleDrawer = () => {
        setDrawerOpen(!drawerOpen);
    };

    const handleTrainingImageClick = () => {
        imageFileInputRef.current?.click();
    };

    const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !acceptedImageMimeTypes.includes(file.type)) return;

        const htmlImage = await convertImageFileToHtmlImage(file);
        onLessonDataChange("image", {
            ...lessonFormData,
            image: { file, width: htmlImage.naturalWidth, height: htmlImage.naturalHeight }
        });
    };

    const handleTrainingNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onLessonDataChange("lessonName", { ...lessonFormData, lessonName: event.target.value });
    };

    const handleVersionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onLessonDataChange("lessonVersionName", { ...lessonFormData, lessonVersionName: event.target.value });
    };

    const handleTagAChange = (event: React.SyntheticEvent, newTagA: string | null) => {
        onLessonDataChange("tagA", { ...lessonFormData, tagA: newTagA });
    };

    const handleTagBChange = (event: React.SyntheticEvent, newTagB: string | null) => {
        onLessonDataChange("tagB", { ...lessonFormData, tagB: newTagB });
    };

    const handleMaxPointsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = event.target.value.replace(/[^0-9]/g, '');
        const inputValue = parseInt(sanitized, 10);
        onLessonDataChange("maxPoints", { ...lessonFormData, maxPoints: isNaN(inputValue) ? 0 : inputValue });
    };

    const handleLessonTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseInt(event.target.value, 10) as LessonType;
        onLessonDataChange("lessonType", { ...lessonFormData, lessonType: value });
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onLessonDataChange("description", { ...lessonFormData, description: event.target.value });
    };

    const handleShuffleAnswersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onLessonDataChange("shuffleAnswers", { ...lessonFormData, shuffleAnswers: event.target.checked });
    };

    const handleFeedbackChange = (event: React.ChangeEvent<HTMLInputElement>, rewardType: RewardType) => {
        const key = RewardType[rewardType].toLowerCase() as keyof Feedback;
        const text = event.target.value;
        onLessonDataChange("feedback", {
            ...lessonFormData,
            feedback: {
                ...lessonFormData.feedback,
                [key]: text ? { feedback: text } : null
            }
        });
    };

    const imagePreviewUrl = lessonFormData.image
        ? URL.createObjectURL(lessonFormData.image.file)
        : null;

    const feedbackLevels: RewardType[] = [
        RewardType.Gold,
        RewardType.Silver,
        RewardType.Bronze,
        RewardType.None,
    ];

    return (
        <Box>
            <ToggleDrawerButton
                drawerOpen={drawerOpen}
                variant="outlined"
                startIcon={drawerOpen ? <WestIcon/> : <EastIcon/>}
                onClick={toggleDrawer}
            >
                {drawerOpen ? t("lessonEditor.hideDetails") : t("lessonEditor.showDetails")}
            </ToggleDrawerButton>
            <Drawer
                disableEnforceFocus
                PaperProps={{ style: { width: "28%", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
                slotProps={{ backdrop: { sx: { backgroundColor: "transparent", backdropFilter: "blur(2px)" } } }}
                anchor="left"
                open={drawerOpen}
                onClose={toggleDrawer}
            >
                <FormContainerBox>

                    {/* LESSON-LEVEL SECTION */}
                    <TitleTypography>{title}</TitleTypography>
                    <SubtitleTypography theme={theme}>{t("lessonEditor.subtitle")}</SubtitleTypography>

                    <SpacedTypography>{t("lessonEditor.addImage")}</SpacedTypography>
                    <ImageUploadBox theme={theme} onClick={handleTrainingImageClick}>
                        <StyledHiddenInput
                            type="file"
                            ref={imageFileInputRef}
                            accept={acceptedImageMimeTypes}
                            onClick={(event) => (event.target as HTMLInputElement).value = ''}
                            onChange={handleImageChange}
                        />
                        {imagePreviewUrl ? (
                            <ImagePreview src={imagePreviewUrl} alt="Lesson Avatar"/>
                        ) : (
                            <ImagePlaceholderBox>
                                <InsertPhotoOutlinedIcon fontSize="large"/>
                            </ImagePlaceholderBox>
                        )}
                    </ImageUploadBox>
                    <ErrorTypography color="error">{formValidationErrors.image}</ErrorTypography>

                    <FieldTypography>{t("lessonEditor.addName")}</FieldTypography>
                    <StyledTextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ style: { height: 38 } }}
                        id="training-name"
                        label={t("lessonEditor.name")}
                        value={lessonFormData.lessonName}
                        onChange={handleTrainingNameChange}
                        error={!!formValidationErrors.lessonName}
                        helperText={formValidationErrors.lessonName}
                        autoComplete="off"
                    />

                    <FieldTypography>{t("lessonEditor.addTags")}</FieldTypography>
                    <StyledAutocomplete
                        id="tag-a"
                        freeSolo
                        options={tags.tagsA}
                        value={lessonFormData.tagA ?? ''}
                        onChange={handleTagAChange}
                        onInputChange={handleTagAChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label={t("lessonEditor.tagA")}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { height: '38px' }
                                }}
                            />
                        )}
                    />
                    <StyledAutocomplete
                        id="tag-b"
                        freeSolo
                        options={tags.tagsB}
                        value={lessonFormData.tagB ?? ''}
                        onChange={handleTagBChange}
                        onInputChange={handleTagBChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                label={t("lessonEditor.tagB")}
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { height: '38px' }
                                }}
                            />
                        )}
                    />

                    <StyledDivider/>

                    {/* VERSION-LEVEL SECTION */}
                    {showVersionChip && (
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <SubtitleTypography theme={theme}>
                                {t("lessonEditor.version.subtitle")}:
                            </SubtitleTypography>
                            <Chip
                                label={lessonFormData.language?.toUpperCase()}
                                size="small"
                            />
                        </Box>
                    )}

                    {isAdmin && (
                        <>
                            <FieldTypography>{t("lessonEditor.version.lessonType")}</FieldTypography>
                            <StyledTextField
                                select
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ style: { height: 38 } }}
                                id="lesson-type"
                                label={t("lessonEditor.version.lessonTypeLabel")}
                                value={lessonFormData.lessonType}
                                onChange={handleLessonTypeChange}
                                autoComplete="off"
                                disabled={disableLessonTypeChange}
                            >
                                {Object.entries(LessonType).filter(([key]) => isNaN(Number(key))).map(([key, value]) => (
                                    <MenuItem key={key} value={value}>
                                        {t(`lessonEditor.version.lessonTypes.${key}`)}
                                    </MenuItem>
                                ))}
                            </StyledTextField>
                        </>
                    )}

                    <FieldTypography>{t("lessonEditor.version.addName")}</FieldTypography>
                    <StyledTextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ style: { height: 38 } }}
                        id="version-name"
                        label={t("lessonEditor.version.name")}
                        value={lessonFormData.lessonVersionName}
                        onChange={handleVersionNameChange}
                        error={!!formValidationErrors.lessonVersionName}
                        helperText={formValidationErrors.lessonVersionName}
                        autoComplete="off"
                    />

                    <FieldTypography>{t("lessonEditor.version.addScore")}</FieldTypography>
                    <StyledTextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ style: { height: 38 } }}
                        id="max-score"
                        label={t("lessonEditor.version.maxScore")}
                        onChange={handleMaxPointsChange}
                        value={lessonFormData.maxPoints}
                        error={!!formValidationErrors.maxPoints}
                        helperText={formValidationErrors.maxPoints}
                        autoComplete="off"
                    />

                    {lessonFormData.lessonType === LessonType.VR && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={lessonFormData.shuffleAnswers}
                                    onChange={handleShuffleAnswersChange}
                                />
                            }
                            label={t("lessonEditor.version.shuffleAnswers")}
                        />
                    )}

                    <FieldTypography>{t("lessonEditor.version.addTrainingDescription")}</FieldTypography>
                    <DescriptionTextField
                        id="description"
                        label={t("lessonEditor.version.addDescription")}
                        InputLabelProps={{ shrink: false, style: { display: "none" } }}
                        multiline
                        rows={6}
                        onChange={handleDescriptionChange}
                        value={lessonFormData.description ?? ''}
                        autoComplete="off"
                    />

                   {feedbackLevels.map((rewardType) => {
                    const rewardTypeText = RewardType[rewardType].toLowerCase() as keyof Feedback;
                    const feedbackValue = lessonFormData.feedback?.[rewardTypeText]?.feedback ?? "";
                    const isFeedbackTooLong = feedbackValue.length > FEEDBACK_MAX_LENGTH;

                    return (
                        <React.Fragment key={rewardType}>
                        <FieldTypography>
                            {t(`lessonEditor.version.feedback.${rewardTypeText}`)}:
                        </FieldTypography>

                        <DescriptionTextField
                            id={`feedback-${rewardTypeText}`}
                            label={t("lessonEditor.version.addDescription")}
                            InputLabelProps={{ shrink: false, style: { display: "none" } }}
                            multiline
                            rows={6}
                            onChange={(e) => handleFeedbackChange(e as React.ChangeEvent<HTMLInputElement>, rewardType)}
                            value={feedbackValue}
                            autoComplete="off"
                            error={isFeedbackTooLong}
                            helperText={isFeedbackTooLong ? t("lessonEditor.formValidationError.feedbackTooLong", { max: FEEDBACK_MAX_LENGTH }) : `${feedbackValue.length}/${FEEDBACK_MAX_LENGTH}`}
                        />
                        </React.Fragment>
                    );
                    })}
                </FormContainerBox>
            </Drawer>
        </Box>
    );
};